import React from 'react'
import styled from '@emotion/styled'
import {last, pathOr, pluck} from 'ramda'

import {cssTheme} from '@vio/atlas/themes'

const HistogramElement = styled.div<{height: string}>`
  display: flex;
  align-items: flex-end;
  flex-flow: row wrap;
  height: ${({height}) => height};
  padding: 0 ${cssTheme.layout.spacing.s500};
  gap: ${cssTheme.layout.spacing.s100};
`

const HistogramItemElement = styled.div<{active: boolean}>`
  flex-grow: 1;
  transition: height 0.3s ease-in-out;
  height: 0;
  border-radius: 2px 2px 0 0;
  background-color: ${({active}) =>
    active
      ? cssTheme.colors.background.brand.primary
      : cssTheme.colors.background.neutral.tertiary};
`

interface HistogramPropsType {
  /** Histogram height */
  height?: string

  /** Histogram item size */
  step: number

  /** Histogram active value */
  value: Array<number | null | undefined>

  /** Histogram values */
  values: Array<{id: string; value: number}>
}

const isActive = (bkt: number, min: number, max: number): boolean => {
  return bkt >= min && bkt <= max
}

const calculateItemHeight = (value: number, maxValue: number): number => {
  return (value / maxValue) * 100
}

const DEFAULT_HISTOGRAM_HEIGHT = '38px'

export const Histogram: React.FC<HistogramPropsType> = ({
  values = [],
  value = [],
  step = 1,
  height = DEFAULT_HISTOGRAM_HEIGHT
}) => {
  const [isRendered, setIsRendered] = React.useState<boolean>(false)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsRendered(true)
      clearTimeout(timer)
    }, 10)
    return () => clearTimeout(timer)
  }, [])

  const [
    min = pathOr(0, [0, 'id'], values),
    max = pathOr(step, ['id'], last(values)) * step
  ] = value
  const maxValue = Math.max(...pluck('value', values))
  const minActiveBkt = Math.round(min / step)
  const maxActiveBkt = Math.round(max / step)

  return (
    <HistogramElement height={height}>
      {values.map(item => {
        const itemHeight = isRendered
          ? calculateItemHeight(item.value, maxValue)
          : 0

        return (
          <HistogramItemElement
            key={item.id}
            active={isActive(Number(item.id), minActiveBkt, maxActiveBkt)}
            style={{height: `${itemHeight}%`}}
          />
        )
      })}
    </HistogramElement>
  )
}
